import { ApiError, type TemplateDefinition } from "@/api/client";
import { margins, pageSizeMM, type Field } from "@/features/templates/designer/geometry";

/** One thing wrong with the definition, addressed as internal/render/validate.go does, e.g. "fields[3].font". */
export type Problem = { path: string; issue: string };

export type Target =
  | { panel: "field"; index: number; key: string }
  | { panel: "page"; key: string }
  | { panel: "layers"; key: string }
  | { panel: "general" };

/** Where a problem is shown: on a field and its inspector input, in a panel, or only in the list. */
export function targetOf(path: string): Target {
  const field = /^fields\[(\d+)\](?:\.(.+))?$/.exec(path);
  if (field) return { panel: "field", index: Number(field[1]), key: field[2] ?? "" };
  if (path === "page" || path.startsWith("page.")) return { panel: "page", key: path.slice("page.".length) };
  if (/^(background|watermark)(\.|$)/.test(path)) return { panel: "layers", key: path };
  return { panel: "general" };
}

/** A refused save's or publish's problems; the API puts each path in `details[].field`. */
export function serverProblems(error: unknown): Problem[] {
  if (!(error instanceof ApiError) || error.status !== 422) return [];
  return error.details.map((d) => ({ path: d.field ?? "", issue: d.issue ?? "" }));
}

const mm = (v: number) => String(Math.round(v * 10) / 10);

/**
 * A quick subset of the server's checks (page, margins, opacity, dimensions, extents), so an obvious
 * mistake shows while editing. Fonts, images and binds are left to the server, which stays the authority.
 */
export function checkDefinition(def: TemplateDefinition): Problem[] {
  const out: Problem[] = [];
  const add = (path: string, issue: string) => out.push({ path, issue });
  const size = pageSizeMM(def.page);
  if (!size) {
    const known = ["A3", "A4", "A5", "LETTER", "LEGAL"].includes(String(def.page.size).trim().toUpperCase());
    add(known ? "page.orientation" : "page.size", known ? "must be portrait or landscape" : "must be one of A3, A4, A5, Letter, Legal");
  }
  const m = margins(def.page);
  let marginsOK = true;
  for (const side of ["top", "right", "bottom", "left"] as const) {
    if (m[side] < 0) {
      add(`page.margins_mm.${side}`, "must not be negative");
      marginsOK = false;
    }
  }
  if (size && marginsOK && (m.left + m.right >= size.w || m.top + m.bottom >= size.h)) add("page.margins_mm", "leave no room on the page");
  const opacity = def.watermark?.opacity;
  if (def.watermark && (typeof opacity !== "number" || !(opacity >= 0 && opacity <= 1))) add("watermark.opacity", "must be between 0 and 1");
  def.fields.forEach((field, i) => checkField(`fields[${i}]`, field, size, Math.max(m.right, 0), add));
  return out;
}

function checkField(
  path: string,
  f: Field,
  size: { w: number; h: number } | null,
  rightMargin: number,
  add: (path: string, issue: string) => void,
) {
  const position = (key: "x_mm" | "y_mm", limit: number | undefined) => {
    const v = f[key];
    if (limit !== undefined && (v < 0 || v >= limit)) add(`${path}.${key}`, `must be within the page (0 to ${mm(limit)} mm)`);
    else if (v < 0) add(`${path}.${key}`, "must not be negative");
    else return true;
    return false;
  };
  const xOK = position("x_mm", size?.w);
  const yOK = position("y_mm", size?.h);
  const positive = (key: "w_mm" | "h_mm" | "size_mm" | "size_pt", required: boolean) => {
    const v = f[key];
    if (v === undefined) {
      if (required) add(`${path}.${key}`, "is required");
      return undefined;
    }
    if (!(v > 0)) {
      add(`${path}.${key}`, "must be greater than 0");
      return undefined;
    }
    return v;
  };
  let w: number | undefined;
  let h: number | undefined;
  let wKey = "w_mm";
  let hKey = "h_mm";
  if (f.type === "text") {
    positive("size_pt", false);
    w = positive("w_mm", false);
    if (f.w_mm === undefined && size && xOK && f.x_mm >= size.w - rightMargin) {
      add(`${path}.x_mm`, `must be left of the right margin (${mm(size.w - rightMargin)} mm); move it or set w_mm`);
    }
  } else if (f.type === "qr") {
    w = h = positive("size_mm", true);
    wKey = hKey = "size_mm";
  } else {
    w = positive("w_mm", true);
    h = positive("h_mm", true);
  }
  if (size && xOK && w !== undefined && f.x_mm + w > size.w + 1e-9) add(`${path}.${wKey}`, "runs past the right edge of the page");
  if (size && yOK && h !== undefined && f.y_mm + h > size.h + 1e-9) add(`${path}.${hKey}`, "runs past the bottom edge of the page");
}
